import './SkuArtFile.less';
import { useMemoizedFn, useMount } from 'ahooks';
import { Progress, Tooltip, Upload } from 'antd';
import {
  RcFile,
  UploadChangeParam,
  UploadFile,
} from 'antd/lib/upload/interface';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import axios, { CancelTokenSource } from 'axios';
import {
  AiIcon,
  DefaultFileTypeIcon,
  DocIcon,
  DocxIcon,
  EpsIcon,
  ErrorExclamationPointIcon,
  GifIcon,
  IdIcon,
  JpegIcon,
  JpgIcon,
  PdfIcon,
  PngIcon,
  PptIcon,
  PptxIcon,
  PsdIcon,
  RarIcon,
  SvgIcon,
  TifIcon,
  TiffIcon,
  UploadDeleteIcon,
  UploadDownIcon,
  XlsIcon,
  XlsxIcon,
  ZipIcon,
} from 'components/Icons';
import { RevxButton } from 'components/RevxButton';
import RevxFlexLayout from 'components/RevxFlexLayout';
import { error, toast } from 'components/RevxMessage';
import { UnderlineText } from 'components/RevxTypography';
import {
  CommonUploadFileData,
  FileItem,
  UploadAction,
} from 'domains/workspaceDetailDomain';
import { cloneDeep } from 'lodash-es';
import React, {
  CSSProperties,
  ReactElement,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import workspaceDetailService from 'services/workspaceDetailService';
import {
  JOB_FILE_MAX_NUM,
  PICTURE_REGULAR,
  UPLOAD_ACCEPT,
  UPLOAD_FILE_ADDRESS_PREFIX,
  UPLOAD_FILE_NAME_MAX_LENGTH,
  UPLOAD_FILE_THUMBNAIL_ADDRESS_PREFIX,
} from 'utils/constants';
import { getImage } from 'utils/gallery';
import {
  useSortable,
  arrayMove,
  SortableContext,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
} from '@dnd-kit/core';
import { downloadFileHandler } from '../../../../../utils/misc';

interface DraggableUploadListItemProps {
  originNode: React.ReactElement<
    any,
    string | React.JSXElementConstructor<any>
  >;
  file: UploadFile;
  fileList: UploadFile<FileItem>[];
  actions: UploadAction;
}

const SkuArtFile = ({
  defaultFileList,
  onUploadFileChange,
  showUploadList,
  showUploadButton,
  itemData,
  deleteFileHandler,
  saveFileHandler,
  setIsUploading,
  onFileListChange,
}: Partial<CommonUploadFileData>) => {
  const [fileList, setFileList] = useState<UploadFile<FileItem>[]>(
    defaultFileList || []
  );
  const cancelSources = useRef<{ source: CancelTokenSource; uid: string }[]>(
    []
  );
  const [fileErrorList, setFileErrorList] = useState<string[]>([]);
  const isUploading = useAppSelector((state) => state.app.isUploading);
  const fileMaxLength = JOB_FILE_MAX_NUM;

  const dispatch = useAppDispatch();

  const { t } = useTranslation();

  const sensor = useSensor(PointerSensor, {
    activationConstraint: { distance: 10 },
  });

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (active.id !== over?.id) {
      const l = (() => {
        const list = [...fileList];
        const activeIndex = list.findIndex((i) => i.uid === active.id);
        const overIndex = list.findIndex((i) => i.uid === over?.id);
        return arrayMove(list, activeIndex, overIndex);
      })();
      setFileList(l);
      onFileListChange?.(l);
    }
  };

  const onCancelUpload = (uid?: string) => {
    cancelSources.current.forEach((item) => {
      if (item.uid === uid) {
        item?.source?.cancel();
      }
    });
  };

  const fileTypeIcon: { [str: string]: ReactElement } = {
    ai: <AiIcon />,
    doc: <DocIcon />,
    docx: <DocxIcon />,
    eps: <EpsIcon />,
    gif: <GifIcon />,
    indd: <IdIcon />,
    jpeg: <JpegIcon />,
    jpg: <JpgIcon />,
    pdf: <PdfIcon />,
    png: <PngIcon />,
    ppt: <PptIcon />,
    pptx: <PptxIcon />,
    psd: <PsdIcon />,
    rar: <RarIcon />,
    svg: <SvgIcon />,
    tif: <TifIcon />,
    tiff: <TiffIcon />,
    xls: <XlsIcon />,
    xlsx: <XlsxIcon />,
    zip: <ZipIcon />,
  };

  useMount(() => {
    return () => {
      dispatch.app.setState({
        isUploading: false,
      });
    };
  });

  useEffect(() => {
    setFileList(defaultFileList || []);
  }, [defaultFileList]);

  const uploadButton = (
    <div className="upload-select">
      <RevxFlexLayout direction="column">
        <span>Drag and drop</span>
        <span>or</span>
        <UnderlineText color="blue">Click to Upload</UnderlineText>
      </RevxFlexLayout>
    </div>
  );

  const handleChange = ({ fileList }: UploadChangeParam<UploadFile<any>>) => {
    if (fileList.length > fileMaxLength) {
      return false;
    }
    if (fileList[0]?.response) {
      fileList[0].thumbUrl = fileList[0].response.thumbUrl;
    }
    if (
      fileList[fileList.length - 1]?.status === 'uploading' &&
      fileList.filter((item) => item.status !== 'uploading').length
    ) {
      fileList = [
        ...fileList.filter((item) => item.status !== 'uploading'),
        ...fileList.filter((item) => item.status === 'uploading').reverse(),
      ];
    }
    if (typeof setIsUploading === 'function') {
      setIsUploading(
        !!fileList.filter((item) => item.status === 'uploading').length
      );
    }
    setFileList(fileList.filter((item) => item.status !== 'error'));
  };

  const handleDownload = async (option: any) => {
    dispatch.app.showLoading();
    const payload = option.response || option;
    downloadFileHandler(
      `${UPLOAD_FILE_ADDRESS_PREFIX}${payload.url}`,
      payload.name
    );
    dispatch.app.hideLoading();
  };

  const handleRemove = async (option: UploadFile<FileItem>) => {
    const payload = option.response || option;
    if (typeof deleteFileHandler === 'function') {
      const res = await deleteFileHandler(payload);
      if (res === '') {
        return false;
      } else {
        if (onUploadFileChange && typeof onUploadFileChange === 'function') {
          onUploadFileChange(fileList);
        }
        return true;
      }
    }
    return false;
  };

  const handleBeforeUpload = (file: RcFile, choicefileList: RcFile[]) => {
    if (1 + fileList.length > fileMaxLength) {
      error(t('common.fileMaxLengthMessage'));
      return Promise.reject();
    }

    if (file.name.length > UPLOAD_FILE_NAME_MAX_LENGTH) {
      error(
        t('common.fileMaxNameMessage', {
          length: UPLOAD_FILE_NAME_MAX_LENGTH,
        })
      );
      return Promise.reject();
    }

    if (!file.size) {
      error(t('common.fileEmptyMessage'));
      return Promise.reject();
    }

    return true;
  };

  const stepPrint = async (
    path: string,
    maxErrorNum: number,
    resolve: { (value: unknown): void }
  ) => {
    if (maxErrorNum > 20) {
      resolve(true);
    } else {
      try {
        maxErrorNum++;
        await new Promise((resolve) => setTimeout(resolve, 1000));
        await axios.get(`${UPLOAD_FILE_THUMBNAIL_ADDRESS_PREFIX}${path}`);
        resolve(true);
      } catch {
        stepPrint(path, maxErrorNum, resolve);
      }
    }
  };

  const handleRequest = async (option: any) => {
    // s3 upload
    dispatch.app.setState({
      isUploading: true,
    });
    const fileUrl = await dispatch.uploadFile.getPresigned([option.file.name]);
    const uploadFileUrl = fileUrl[0].path;

    const source = axios.CancelToken.source();
    cancelSources.current.push({
      source,
      uid: option?.file?.uid,
    });

    const uploadRes = await workspaceDetailService.uploadFileToWorkSpace(
      uploadFileUrl,
      option.file,
      {
        token: source.token,
      },
      (param: ProgressEvent) => {
        option.onProgress({
          percent: Math.ceil((param.loaded / option.file.size) * 99),
        });
      }
    );
    if (!uploadRes || uploadRes.status !== 200) {
      option.onError();
      dispatch.app.setState({
        isUploading: false,
      });
      return false;
    }
    const uploadFileUrlObject = new URL(uploadFileUrl);
    const fileRes = {
      path: uploadFileUrlObject.pathname.substr(1),
      fileName: option.file.name,
    };

    const fileExtension = option.file.name.split('.').pop() || '';
    if (fileExtension === 'pdf') {
      await new Promise((resolve) =>
        stepPrint(uploadFileUrlObject.pathname.substr(1), 0, resolve)
      );
    }

    // save handle
    if (typeof saveFileHandler === 'function') {
      const fileItem = await saveFileHandler({
        ...fileRes,
        ...itemData,
      });
      option.onSuccess(fileItem);
    }

    const newFileList = cloneDeep(fileList).map((item) => {
      if (item.name === option.file.name) {
        item.url = uploadFileUrlObject.pathname.substr(1);
        item.status = 'done';
      }
      return item;
    });
    setFileList(newFileList);
    if (onUploadFileChange && typeof onUploadFileChange === 'function') {
      onUploadFileChange(newFileList);
    }
    dispatch.app.setState({
      isUploading: false,
    });
  };

  const renderFileType = (fileName: string, file: UploadFile<any>) => {
    const fileExtension = fileName.split('.').pop() || '';
    if (fileExtension === 'pdf') {
      if (fileErrorList.includes(fileName)) {
        return (
          <span className="anticon anticon-file">
            {fileTypeIcon[fileExtension]}
          </span>
        );
      } else {
        return (
          <img
            className="ant-upload-list-item-image"
            onError={() => {
              setFileErrorList([...fileErrorList, fileName]);
            }}
            src={`${UPLOAD_FILE_THUMBNAIL_ADDRESS_PREFIX}${
              file.url || file.response?.fileUrl
            }`}
            alt=""
          />
        );
      }
    } else if (Object.keys(fileTypeIcon).includes(fileExtension)) {
      return (
        <span className="anticon anticon-file">
          {fileTypeIcon[fileExtension]}
        </span>
      );
    } else {
      return (
        <span className="anticon anticon-file">
          <DefaultFileTypeIcon />
        </span>
      );
    }
  };

  const errorFileHandler = () => {
    toast(t('common.errorDownFile'));
    if (onUploadFileChange && typeof onUploadFileChange === 'function') {
      onUploadFileChange(fileList);
    }
  };

  const DraggableItemRender = ({
    originNode,
    file,
    fileList,
    actions,
  }: DraggableUploadListItemProps) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({
      id: file.uid,
    });
    const currentIndex =
      fileList.findIndex((item) => item.uid === file.uid) + 1;
    const style: React.CSSProperties = {
      transform: CSS.Transform.toString(transform),
      transition,
      cursor: 'move',
    };
    const sortableItemDraggingStyles: CSSProperties = isDragging
      ? {
          zIndex: 999,
          position: 'relative',
        }
      : {};
    const filePath = getImage(file.url || '');
    const fileName =
      file.fileName || file.name || file.url || file.thumbUrl || '';
    const isFirstImg = file.url === fileList?.[0]?.url;
    const imageTile = () => {
      if (isFirstImg) return 'Image 1 - SKU thumbnail';
      return `Image ${currentIndex}`;
    };
    return (
      <>
        {file.status === 'done' && (
          <div
            className="draggable-item"
            ref={setNodeRef}
            style={{ ...style, ...sortableItemDraggingStyles }}
          >
            <RevxFlexLayout className="image-title" justify="justify-center">
              {imageTile()}
            </RevxFlexLayout>
            <div className="ant-upload-list-item ant-upload-list-item-done ant-upload-list-item-list-type-picture-card">
              <div className="ant-upload-list-item-info">
                <span className="ant-upload-span">
                  <div
                    {...listeners}
                    {...attributes}
                    className={`ant-upload-list-item-thumbnail ${
                      !PICTURE_REGULAR.test(fileName) ||
                      fileErrorList.includes(fileName)
                        ? 'ant-upload-list-item-file'
                        : ''
                    }`}
                  >
                    {PICTURE_REGULAR.test(fileName) &&
                    !fileErrorList.includes(fileName) ? (
                      <img
                        className="ant-upload-list-item-image"
                        src={filePath}
                        alt={file.fileName}
                        onError={() => {
                          setFileErrorList([...fileErrorList, fileName]);
                        }}
                      />
                    ) : (
                      <>{renderFileType(fileName, file)}</>
                    )}
                  </div>
                  <Tooltip title={fileName}>
                    <span className="ant-upload-list-item-name">
                      {fileName}
                    </span>
                  </Tooltip>
                </span>
              </div>
              <div className="ant-upload-list-item-actions">
                <button
                  type="button"
                  className="ant-btn ant-btn-text ant-btn-sm ant-btn-icon-only ant-upload-list-item-card-actions-btn"
                  hidden={!isFirstImg}
                >
                  <Tooltip title="This file will be as Job image">
                    <span>
                      <ErrorExclamationPointIcon />
                    </span>
                  </Tooltip>
                </button>
                <button
                  type="button"
                  className="ant-btn ant-btn-text ant-btn-sm ant-btn-icon-only ant-upload-list-item-card-actions-btn"
                  onClick={() => {
                    axios
                      .get(filePath || '')
                      .then((res) => {
                        if (res.status === 200) {
                          window.open(filePath);
                        } else {
                          errorFileHandler();
                        }
                      })
                      .catch((error) => {
                        if ((error + '').indexOf('404') >= 0) {
                          errorFileHandler();
                        } else {
                          window.open(filePath);
                        }
                      });
                  }}
                >
                  <span>
                    <UploadDownIcon />
                  </span>
                </button>
                {showUploadButton && (
                  <button
                    type="button"
                    className="ant-btn ant-btn-text ant-btn-sm ant-btn-icon-only ant-upload-list-item-card-actions-btn"
                    onClick={() => actions.remove(file)}
                    hidden={typeof deleteFileHandler === 'undefined'}
                  >
                    <span>
                      <UploadDeleteIcon />
                    </span>
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
        {file.status === 'uploading' && (
          <div>
            <div className="image-title"></div>
            <div className="ant-upload-list-item ant-upload-list-item-uploading ant-upload-list-item-list-type-picture-card">
              <div className="ant-upload-list-item-info">
                <span className="ant-upload-list-item-thumbnail">
                  {t('common.uploading')}
                  {/* <LogoImg /> */}
                </span>
              </div>
              <div className="ant-upload-list-item-progress">
                <Progress
                  percent={file.percent}
                  size="small"
                  showInfo={false}
                />
              </div>
              <RevxButton
                className="ant-upload-list-item-cancel"
                brandOrange
                type="primary"
                size="small"
                onClick={() => {
                  onCancelUpload(file?.originFileObj?.uid);
                }}
              >
                Cancel
              </RevxButton>
            </div>
          </div>
        )}
      </>
    );
  };

  const dragOverHandler = useMemoizedFn((e) => {
    if (e?.currentTarget?.classList) {
      e.currentTarget.classList.add('drag-over');
    }
  });
  const dragLeaveHandler = useMemoizedFn((e) => {
    if (e?.currentTarget?.classList) {
      e.currentTarget.classList.remove('drag-over');
    }
  });
  const dropHandler = useMemoizedFn((e) => {
    if (e?.currentTarget?.classList) {
      e.currentTarget.classList.remove('drag-over');
    }
    if (e?.dataTransfer?.files?.length > 1) {
      toast.error(t('common.addOneFile'));
    }
  });

  const divRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const selectDiv = divRef.current?.querySelector(
      '.ant-upload-select'
    ) as HTMLDivElement;

    selectDiv.addEventListener('dragover', dragOverHandler);
    selectDiv.addEventListener('dragleave', dragLeaveHandler);
    selectDiv.addEventListener('drop', dropHandler);

    return () => {
      selectDiv.removeEventListener('dragover', dragOverHandler);
      selectDiv.removeEventListener('dragLeave', dragLeaveHandler);
      selectDiv.removeEventListener('drop', dropHandler);
    };
  });

  return (
    <div className="sku-artFile" ref={divRef}>
      <DndContext sensors={[sensor]} onDragEnd={onDragEnd}>
        <SortableContext
          items={fileList.map((i) => i.uid)}
          strategy={horizontalListSortingStrategy}
        >
          <Upload
            disabled={isUploading}
            accept={UPLOAD_ACCEPT}
            listType="picture-card"
            fileList={fileList}
            onChange={handleChange}
            customRequest={handleRequest}
            onDownload={handleDownload}
            onRemove={handleRemove}
            beforeUpload={handleBeforeUpload}
            itemRender={(originNode, file, fileList, actions) => {
              return (
                <DraggableItemRender
                  file={file}
                  fileList={fileList}
                  originNode={originNode}
                  actions={actions}
                />
              );
            }}
            showUploadList={
              showUploadList || {
                showPreviewIcon: false,
                showDownloadIcon: true,
                showRemoveIcon: !!showUploadButton,
              }
            }
            progress={{
              status: 'active',
            }}
          >
            {showUploadButton &&
              fileList?.length < fileMaxLength &&
              uploadButton}
          </Upload>
        </SortableContext>
      </DndContext>
    </div>
  );
};

export default SkuArtFile;
